import React, { createContext, useContext, useState } from 'react';

// App-level UI state shared across the shell. Routing lives in
// src/navigation/ (React Navigation) — this context is intentionally
// navigation-free: just the topbar switchers, locale, and sidebar density.

// Lightweight shapes for the topbar switchers (real values come from the
// session org + live locations; these are just the state container types).
export interface TenantRef { id: string; name: string }
export interface LocationRef { id: string; name: string }

export type Density = 'default' | 'comfortable' | 'compact';

interface AppContextValue {
  tenant: TenantRef;
  setTenant: (t: TenantRef) => void;
  location: LocationRef;
  setLocation: (l: LocationRef) => void;
  /** Bumped whenever the locations list changes (create/update/delete) so the
   *  topbar location switcher refetches without a full page refresh. */
  locationsVersion: number;
  refreshLocations: () => void;
  locale: string;
  setLocale: (l: string) => void;
  density: Density;
  collapsed: boolean;
  setCollapsed: (c: boolean) => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [tenant, setTenant] = useState<TenantRef>({ id: '', name: 'My Org' });
  const [location, setLocation] = useState<LocationRef>({ id: 'all', name: 'All locations' });
  const [locale, setLocale] = useState('en-IN');
  const [collapsed, setCollapsed] = useState(false);
  const [locationsVersion, setLocationsVersion] = useState(0);
  const refreshLocations = () => setLocationsVersion((v) => v + 1);

  return (
    <AppContext.Provider value={{
      tenant, setTenant,
      location, setLocation,
      locationsVersion, refreshLocations,
      locale, setLocale,
      density: 'default',
      collapsed, setCollapsed,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
